import React, { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Identifiable } from '@custis/common';
import { Checkbox, Input, Popover } from 'antd';
import { MouseActions, SELECTION_COLUMN_ID, TTableColumn } from '../../models';
import s from './VisibleColumns.module.less';
import { Theme, presetGpnDefault } from '@consta/uikit/Theme';
import { IdType } from 'react-table';
import { observer } from 'mobx-react';
import { SearchOutlined } from '@ant-design/icons';
import { SettingIcon } from '../grid-settings/SettingIcon';

interface Props<T extends Identifiable, TFilter, TSorting> {
  columns: TTableColumn<T, TFilter, TSorting>[];
  hiddenColumns: IdType<T>[];

  isColumnVisible(id?: IdType<T>): boolean | undefined;

  toggleHidden(id?: IdType<T>, visible?: boolean, touched?: boolean): void;

  toggleAll(value: boolean): void;
}

const VisibleColumnsContent = observer(
  <T extends Identifiable, TFilter, TSorting>({
    columns,
    isColumnVisible,
    toggleHidden,
    toggleAll,
    hiddenColumns,
  }: Props<T, TFilter, TSorting>) => {
    const columnsWithoutSelection = useMemo(() => columns.filter((col) => col.id !== SELECTION_COLUMN_ID), [columns]);
    const [visibleColumns, setVisibleColumns] = useState(columns);

    useEffect(() => {
      setVisibleColumns(columnsWithoutSelection);
    }, [columnsWithoutSelection]);

    const onSearch = (e: ChangeEvent<HTMLInputElement>): void => {
      setVisibleColumns(
        columnsWithoutSelection.filter(
          (item) => typeof item.Header === 'string' && item.Header.toLowerCase().includes(e.target.value.toLowerCase()),
        ),
      );
    };

    return (
      <Theme preset={presetGpnDefault}>
        {/* Такое добавление скролла обусловлено тем, что если
        делать через overflow: auto, то постоянно будет появляться-исчезать скролл
        и панель с колонками будет дергаться
       */}
        <div>
          <Input size={'small'} suffix={<SearchOutlined />} onChange={onSearch} className={s.SearchInput} />
          <div>
            <Checkbox checked={hiddenColumns.length === 0} onChange={(e) => toggleAll(e.target.checked)}>
              (Выделить все)
            </Checkbox>
          </div>
          <div className={s.VisibleColumns}>
            {visibleColumns.map((col) => (
              <div key={col.id}>
                <Checkbox
                  checked={Boolean(isColumnVisible(col.id))}
                  onChange={() => {
                    toggleHidden(col.id, !isColumnVisible(col.id), true);
                  }}
                >
                  {col.Header as ReactElement}
                </Checkbox>
              </div>
            ))}
          </div>
        </div>
      </Theme>
    );
  },
);

export const VisibleColumns = <T extends Identifiable, TFilter, TSorting>({
  columns,
  isColumnVisible,
  toggleHidden,
  toggleAll,
  hiddenColumns,
}: Props<T, TFilter, TSorting>) => (
  <Popover
    trigger={MouseActions.Click}
    content={<VisibleColumnsContent {...{ columns, isColumnVisible, hiddenColumns, toggleAll, toggleHidden }} />}
    placement={'bottomRight'}
    overlayStyle={{ width: 240 }}
    overlayClassName={s.Modal}
  >
    <SettingIcon />
  </Popover>
);
